import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtGHS, fmtDateTime } from "@/lib/format";
import { downloadCSV } from "@/lib/csv";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Download, ShieldCheck } from "lucide-react";
import { BASIS_LABEL } from "./common";

const COLS = ["code", "hospital_name", "supplier_name", "raised_at", "product", "ordered", "dispatched", "received", "status", "resolution", "resolution_note", "resolved_by", "resolved_at"]
  .map((key) => ({ key, label: key.replace(/_/g, " ") }));
const flat = (rows) => rows.flatMap((d) => d.lines.map((l) => ({
  ...d, product: l.generic_name, ordered: l.ordered, dispatched: l.dispatched, received: l.received,
  resolution: d.resolution ? BASIS_LABEL[d.resolution.basis] : "", resolution_note: d.resolution?.note || "",
  resolved_by: d.resolution?.by || "", resolved_at: d.resolution?.at || "",
})));
const cell = (a, b) => (a !== b ? "text-rose-600 font-semibold" : "");

export const DiscrepanciesTab = ({ onOpen }) => {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const load = useCallback(() => {
    api.get("/wholesale/discrepancies", { params: status === "all" ? {} : { status } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-44 h-9" data-testid="ws-disc-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="open">Open (pending review)</SelectItem><SelectItem value="resolved">Resolved</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
        </Select>
        <Button variant="outline" size="sm" disabled={!rows.length} onClick={() => downloadCSV(`partner_supply_discrepancies_${status}.csv`, flat(rows), COLS)} data-testid="ws-disc-csv-btn"><Download className="h-3.5 w-3.5 mr-1.5" /> CSV</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
            <tr><th className="py-2 px-2">Order</th><th className="py-2 px-2">Product</th><th className="py-2 px-2 text-right">Ordered</th><th className="py-2 px-2 text-right">Dispatched</th><th className="py-2 px-2 text-right">Received</th><th className="py-2 px-2">Status / resolution</th></tr>
          </thead>
          <tbody>
            {!rows.length && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground"><ShieldCheck className="h-8 w-8 mx-auto mb-2 opacity-40" />No discrepancies</td></tr>}
            {rows.map((d) => d.lines.map((l, i) => (
              <tr key={`${d.id}-${l.product_id}`} className={`align-top ${i === d.lines.length - 1 ? "border-b border-border/60" : ""}`} data-testid={`ws-disc-row-${d.code}-${l.sku || l.product_id}`}>
                <td className="py-1.5 px-2">{!i && <button className="text-left" onClick={() => onOpen(d.order_id)} data-testid={`ws-disc-open-${d.code}`}>
                  <div className="font-mono text-xs underline">{d.code}</div><div className="font-medium">{d.hospital_name}</div>
                  <div className="text-xs text-muted-foreground">{d.supplier_name} · {fmtDateTime(d.raised_at)}</div></button>}</td>
                <td className="py-1.5 px-2">{l.generic_name} <span className="text-xs font-mono text-muted-foreground">{l.sku}</span></td>
                <td className="py-1.5 px-2 text-right font-mono">{l.ordered}</td>
                <td className={`py-1.5 px-2 text-right font-mono ${cell(l.dispatched, l.ordered)}`}>{l.dispatched}</td>
                <td className={`py-1.5 px-2 text-right font-mono ${cell(l.received, l.dispatched)}`}>{l.received}</td>
                <td className="py-1.5 px-2 text-xs">{!i && (<>
                  <StatusBadge value={d.status === "open" ? "discrepancy" : "resolved"} label={d.status === "open" ? "Pending review" : "Resolved"} />
                  {d.resolution && <div className="mt-1 text-muted-foreground">{BASIS_LABEL[d.resolution.basis]} ({fmtGHS(d.resolution.value)}) — {d.resolution.note}<br />{d.resolution.by} · {fmtDateTime(d.resolution.at)}</div>}
                </>)}</td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
